/**
 * `#uc1Chips` — "View constraints on the map" (client §3, design §3).
 *
 * Six exclusive chips: Priority (the three-class category map) and one per constraint.
 * Exclusive on purpose — the client's §3 keeps the launch version to one layer at a time so
 * the map stays readable; layering constraints is post-launch. The chip itself only writes
 * `intel.uc1Chip`; the redraw of `intel:uc1:choropleth` lives in `Uc1.tsx`, beside the
 * `units` rows it reads.
 *
 * `#uc1Legend` under the chips names the classes the active chip actually painted, and one
 * "Not assessed: …" row per distinct reason present on the map — so the hatch on screen is
 * never unexplained.
 */
import { useTranslation } from 'react-i18next';

import { useAppStore } from '../../lib/state';
import { ChipHelp, type ChipHelpClass } from './ChipHelp';
import {
  CATEGORY_COLORS,
  CONSTRAINT_COLORS,
  NEUTRAL_COLOR,
  UC1_CATEGORIES,
  UC1_CHIPS,
  constraintSteps,
  normaliseChip,
  type Uc1Level,
} from './uc1Constants';

/**
 * The swatch rows of a chip's hover card — the chip's own palette, so the card and the map
 * can never show a reader two different colours for one class. Priority has its three
 * categories; every constraint chip has flagged / not flagged / the hatch.
 */
function helpClasses(id: string, t: (key: string) => string): ChipHelpClass[] {
  if (id === 'priority') {
    return UC1_CATEGORIES.map((cat) => ({
      id: cat,
      color: CATEGORY_COLORS[cat]!,
      label: t(`intel.uc1.help.priority.${cat}`),
    }));
  }
  return [
    { id: 'flagged', color: CONSTRAINT_COLORS[id] ?? NEUTRAL_COLOR, label: t(`intel.uc1.help.${id}.flagged`) },
    { id: 'notFlagged', color: NEUTRAL_COLOR, label: t(`intel.uc1.help.${id}.notFlagged`) },
    { id: 'notAssessed', color: null, label: t(`intel.uc1.help.${id}.notAssessed`) },
  ];
}

export interface ConstraintChipsProps {
  level: Uc1Level;
  /** The distinct not-assessed reasons among the units the active chip hatched. */
  reasons: string[];
}

export function ConstraintChips({ level, reasons }: ConstraintChipsProps) {
  const { t } = useTranslation();
  const chip = useAppStore((s) => s.intel.uc1Chip);
  const setIntelUc1Chip = useAppStore((s) => s.setIntelUc1Chip);
  const active = normaliseChip(chip);

  const classes: { id: string; color: string; label: string }[] =
    active === 'priority'
      ? UC1_CATEGORIES.map((cat) => ({
          id: cat,
          color: CATEGORY_COLORS[cat]!,
          label: t(`intel.uc1.category.${cat}`),
        }))
      : level === 'admin0'
        ? constraintSteps(active).map((color, i) => ({
            id: `share-${i}`,
            color,
            label: t(`intel.uc1.legend.share${['Low', 'Mid', 'High'][i]}`),
          }))
        : [
            {
              id: 'flagged',
              color: CONSTRAINT_COLORS[active] ?? NEUTRAL_COLOR,
              label: t('intel.uc1.legend.flagged'),
            },
            { id: 'notFlagged', color: NEUTRAL_COLOR, label: t('intel.uc1.legend.notFlagged') },
          ];

  return (
    <div className="uc1-chipsblock">
      <h5 className="intel-block-title">{t('intel.uc1.chipsTitle')}</h5>
      <div id="uc1Chips" className="uc1-chips" role="group" aria-label={t('intel.uc1.chipsTitle')}>
        {UC1_CHIPS.map((id, i) => {
          const on = id === active;
          return (
            <ChipHelp
              key={id}
              helpId={`uc1ChipHelp-${id}`}
              title={t(`intel.uc1.help.${id}.title`)}
              what={t(`intel.uc1.help.${id}.what`)}
              classes={helpClasses(id, t)}
              align={i >= UC1_CHIPS.length / 2 ? 'right' : 'left'}
            >
              <button
                type="button"
                id={`uc1Chip-${id}`}
                className={on ? 'uc1-chip on' : 'uc1-chip'}
                aria-pressed={on}
                style={
                  id === 'priority'
                    ? undefined
                    : { borderColor: CONSTRAINT_COLORS[id], color: on ? '#fff' : undefined,
                        background: on ? CONSTRAINT_COLORS[id] : undefined }
                }
                onClick={() => setIntelUc1Chip(id)}
              >
                {id === 'priority'
                  ? t('intel.uc1.chip.priority')
                  : t(`intel.uc1.dimension.${id}`, { defaultValue: id })}
              </button>
            </ChipHelp>
          );
        })}
      </div>
      <ul id="uc1Legend" className="uc1-legend">
        {classes.map((c) => (
          <li key={c.id}>
            <span className="uc1-swatch" style={{ background: c.color }} aria-hidden="true" />
            {c.label}
          </li>
        ))}
        {reasons.map((reason) => (
          <li key={reason} data-testid={`uc1-legend-${reason}`}>
            <span className="uc1-swatch uc1-swatch-hatched" aria-hidden="true" />
            {t('intel.uc1.legend.notAssessed', {
              reason: t(`intel.uc1.reason.${reason}`, { defaultValue: reason }),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ConstraintChips;
